/* agentChatSlice.ts */

/* React Toolkit */
import {
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

/* Thunks */
import {
  fetchAllowedAgentModels,
  sendAgentChatQuestion,
} from "src/redux/thunks/agent-chat/agentChatThunk";
import { requestAgentSpec } from "src/redux/thunks/agent-chat/agentSpecThunk";

/* Interfaces */
import {
  AgentChatProps,
  AgentModelsResponse,
} from "src/shared/interfaces/api/AgentChat";
import {
  AgentAnswerLanguage,
  AgentSpecSnapshot,
  AgentSpecResponse,
  DEFAULT_AGENT_ANSWER_LANGUAGE,
} from "src/shared/interfaces/api/AgentSpec";

/* Constants */
import { ErrorMessage } from "src/shared/constants/common/ErrorMessage";

const initialState: AgentChatProps = {
  messages: [],
  allowedModels: [],
  selectedModel: null,
  answerLanguage: DEFAULT_AGENT_ANSWER_LANGUAGE,
  defaultModel: null,
  modelsStatus: "idle",
  isAnswering: false,
  error: null,
  specStatus: "idle",
  specError: null,
  lastSummary: null,
  clarification: null,
  unresolved: [],
  undoSnapshot: null,
  specHistory: [],
};

export const agentChatSlice = createSlice({
  name: "agentChat",
  initialState: initialState,
  reducers: {
    /**
     * Clear the chat conversation.
     */
    clearChat: (state) => {
      state.messages = [];
      state.error = null;
    },
    /**
     * The language the assistant asks questions in. Chosen, not
     * inferred: working it out from the prompt sent German
     * questions to English prompts.
     */
    setAnswerLanguage: (
      state,
      action: PayloadAction<AgentAnswerLanguage>
    ) => {
      state.answerLanguage = action.payload;
    },
    /**
     * Select the LLM model used for new questions.
     */
    setSelectedModel: (
      state,
      action: PayloadAction<string | null>
    ) => {
      /* `null` sends no model, so the deployment's own choice applies. */
      state.selectedModel = action.payload;
    },
    /**
     * Remember the controls as they were before a spec was
     * applied, so the user can undo it.
     */
    setUndoSnapshot: (
      state,
      action: PayloadAction<AgentSpecSnapshot>
    ) => {
      state.undoSnapshot = action.payload;
    },
    /**
     * Drop the undo point, either because it was used or because
     * the user has moved on.
     */
    clearUndoSnapshot: (state) => {
      state.undoSnapshot = null;
      state.lastSummary = null;
      state.unresolved = [];
    },
    /**
     * Start a new conversation. Used when the user is done with the current
     * line of questioning, so an unrelated prompt is not read as a follow-up.
     */
    clearSpecHistory: (state) => {
      state.specHistory = [];
    },
    /**
     * Abandon an open question rather than answering it.
     *
     * A question changes nothing, so there is no undo to offer — but it does
     * leave the exchange open, and while it is open the agent replays it and
     * reads the next prompt as an answer to it. Without this the only ways out
     * of a question the user did not want are to answer it or to reload.
     *
     * Deliberately does not touch `undoSnapshot` or `lastSummary`: those belong
     * to a spec that really was applied earlier, and it stays undoable.
     */
    resetSpecConversation: (state) => {
      state.clarification = null;
      state.specError = null;
      /* Raised in review: unresolved belongs to the exchange being
         abandoned, so leaving it would keep reporting what a discarded
         prompt could not apply. */
      state.unresolved = [];
      state.specHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAllowedAgentModels.pending,
        (state) => {
          state.modelsStatus = "loading";
        }
      )
      .addCase(
        fetchAllowedAgentModels.fulfilled,
        (state, action: PayloadAction<AgentModelsResponse>) => {
          state.allowedModels = action.payload.models;
          state.defaultModel = action.payload.default;
          state.modelsStatus = "success";
          /**
           * `null` means "send no model and let the deployment choose",
           * which is what a user should get. Selecting the first allowed
           * model instead would put everyone on `test`, since that is what
           * /models/allowed lists first — the keyword generator rather than
           * the configured provider model.
           */
          if (
            state.selectedModel &&
            !action.payload.models.includes(state.selectedModel)
          ) {
            state.selectedModel = null;
          }
        }
      )
      .addCase(
        fetchAllowedAgentModels.rejected,
        (state, action) => {
          state.allowedModels = [];
          state.selectedModel = null;
          state.modelsStatus =
            action.payload === "forbidden"
              ? "forbidden"
              : "error";
        }
      )
      .addCase(
        sendAgentChatQuestion.pending,
        (state, action) => {
          state.messages.push({
            role: "user",
            content: action.meta.arg.question,
          });
          state.isAnswering = true;
          state.error = null;
        }
      )
      .addCase(
        sendAgentChatQuestion.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.messages.push({
            role: "assistant",
            content: action.payload,
          });
          state.isAnswering = false;
        }
      )
      .addCase(
        sendAgentChatQuestion.rejected,
        (state, action) => {
          state.isAnswering = false;
          state.error =
            typeof action.payload === "string"
              ? action.payload
              : "An unexpected error occured";
        }
      )
      /**
       * Two lifetimes are kept apart here.
       *
       * `clarification`, `specError` and `unresolved` describe the
       * prompt in flight, so a new request clears them — otherwise
       * the panel shows one prompt's feedback next to another's.
       *
       * `lastSummary` describes the change that is currently applied
       * to the controls, so it lives exactly as long as
       * `undoSnapshot` does and is cleared with it in
       * `clearUndoSnapshot`. Clearing it on a failed or unanswerable
       * follow-up prompt would leave the Undo button with nothing to
       * say it undoes.
       */
      .addCase(requestAgentSpec.pending, (state, action) => {
        state.specStatus = "loading";
        state.specError = null;
        state.unresolved = [];
        /* An open question is deliberately left on screen while the answer is
           in flight, so that cancelling can put things back as they were. It
           is replaced on `fulfilled` either way. */
        state.specHistory.push({
          role: "user",
          content: action.meta.arg.prompt,
        });
      })
      .addCase(
        requestAgentSpec.fulfilled,
        (state, action: PayloadAction<AgentSpecResponse>) => {
          state.specStatus = "success";
          state.clarification = action.payload.clarification;
          state.unresolved = action.payload.unresolved;
          if (action.payload.spec) {
            state.lastSummary = action.payload.summary;
          }
          /**
           * The assistant's turn is whatever it said back — the question it
           * asked, or the summary of what it applied. That is what a
           * follow-up has to be read against.
           */
          const reply =
            action.payload.clarification ??
            (action.payload.spec ? action.payload.summary : "");
          if (reply) {
            state.specHistory.push({
              role: "assistant",
              content: reply,
            });
          }
        }
      )
      .addCase(requestAgentSpec.rejected, (state, action) => {
        state.unresolved = [];
        /* The user cancelled: there is nothing to report, and the prompt they
           abandoned should not linger in the conversation either.
           `meta.aborted` is set by redux-toolkit itself, which rejects an
           aborted thunk before the payload creator can return anything.
         *
         * Cancelling undoes the request, not the conversation. Anything the
         * assistant had asked stays asked — on screen and in the history the
         * next prompt is read against. Clearing only the visible half used to
         * leave the two disagreeing: the user saw no question while the agent
         * still had one open, and "Start over" — the way out — is only offered
         * when a question is showing, so it was not there either. */
        if (action.meta.aborted) {
          state.specStatus = "idle";
          state.specError = null;
          state.specHistory.pop();
          return;
        }
        state.clarification = null;
        state.specStatus = "error";
        state.specError =
          typeof action.payload === "string"
            ? action.payload
            : ErrorMessage.UNEXPECTED_ERROR;
      })
      .addMatcher(
        (action) => action.type === "auth/clearAuthState",
        () => initialState
      );
  },
});

/* Actions */
export const {
  clearChat,
  resetSpecConversation,
  setAnswerLanguage,
  setSelectedModel,
  setUndoSnapshot,
  clearUndoSnapshot,
  clearSpecHistory,
} = agentChatSlice.actions;

/* Reducer */
export default agentChatSlice.reducer;
